// src/models/SingleValueListModel.js

export class SingleValueListModel {
  static ALLOWED_KEYS = [
    "id",
    "companyId",
    "userId",
    "scope",
    "name",
    "descriptions",
    "items",
    "metadata",
  ];

  static ALLOWED_SCOPES = ["system", "company", "user"];

  static ALLOWED_METADATA_KEYS = [
    "createdAt",
    "updatedAt",
    "deletedFlag",
    "createdBy",
    "modifiedBy",
  ];

  constructor(data = {}, { isCreation = false } = {}) {
    if (isCreation) {
      this.id = data.id || SingleValueListModel._buildDocId();
      this.companyId = data.companyId || "";
      this.userId = data.userId || null;
      this.scope = SingleValueListModel.ALLOWED_SCOPES.includes(data.scope)
        ? data.scope
        : "company";
      this.name = data.name || "";
      this.descriptions = data.descriptions || "";
      this.items = Array.isArray(data.items)
        ? data.items.map(SingleValueListModel._normalizeItem).filter(Boolean)
        : [];

      const now = new Date().toISOString();
      this.metadata = {
        createdAt: data.metadata?.createdAt || now,
        updatedAt: data.metadata?.updatedAt || now,
        deletedFlag:
          data.metadata?.deletedFlag !== undefined
            ? data.metadata.deletedFlag
            : false,
        createdBy: data.metadata?.createdBy || data.id || null,
        modifiedBy: data.metadata?.modifiedBy || data.id || null,
      };
    } else {
      for (const key of Object.keys(data)) {
        if (!SingleValueListModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          if (typeof data.metadata !== "object" || data.metadata === null) {
            throw new Error("Metadata must be a valid object.");
          }
          for (const metaKey of Object.keys(data.metadata)) {
            if (!SingleValueListModel.ALLOWED_METADATA_KEYS.includes(metaKey)) {
              throw new Error(
                `Invalid metadata property "${metaKey}" not allowed.`,
              );
            }
          }
          this.metadata = { ...(this.metadata || {}), ...data.metadata };
       } else if (key === "items") {
  this.items = Array.isArray(data.items)
    ? data.items.map(SingleValueListModel._normalizeItem).filter(Boolean)
    : [];
} else if (key === "scope") {
  this.scope = SingleValueListModel.ALLOWED_SCOPES.includes(data.scope)
    ? data.scope
    : "company";
} else {
  this[key] = data[key];
}
      }
    }
  }

  static _buildDocId() {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `svl-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  // ── Accepts THREE shapes:
  //    1. Legacy string value → { label, value: "foo" }
  //    2. Single-user approver (old) → { label, value: { level, email } }
  //    3. Multi-user approver (NEW) → { label, value: { level, users: [...], status? } }
  //
  //  The optional `status` field is preserved when present on shape (3).
  // ──
  static _normalizeItem(raw) {
    if (!raw || typeof raw !== "object") return null;

    const label = raw.label ?? "";
    const value = raw.value ?? "";
    const labelEmpty = String(label).trim() === "";
    const valueIsObject = value !== null && typeof value === "object";

    const valueEmpty = valueIsObject
      ? (!Array.isArray(value.users) || value.users.length === 0) &&
        !value.email &&
        value.level == null
      : String(value).trim() === "";

    if (labelEmpty && valueEmpty) return null;

    // ── Object value (approver shape — new or old) ────────────────
    if (valueIsObject) {
      const levelNum = value.level != null ? Number(value.level) : NaN;
      if (!Number.isFinite(levelNum) || levelNum < 1) return null;

      // NEW multi-user shape (optional status)
      if (Array.isArray(value.users)) {
        const users = value.users
          .map((e) => String(e || "").trim().toLowerCase())
          .filter((e) => e.includes("@"));
        const uniqueUsers = Array.from(new Set(users));
        if (uniqueUsers.length === 0) return null;

        const status = String(value.status || "").trim();

        return {
          label: String(label).trim(),
          value: status
            ? { level: levelNum, users: uniqueUsers, status }
            : { level: levelNum, users: uniqueUsers },
        };
      }

      // LEGACY single-email shape → auto-upgrade to users[]
      const email = String(value.email || "").trim().toLowerCase();
      if (!email.includes("@")) return null;
      return {
        label: String(label).trim(),
        value: { level: levelNum, users: [email] },
      };
    }

    // ── Legacy string value ───────────────────────────────────────
    return {
      label: String(label).trim(),
      value: String(value).trim(),
    };
  }

  // ── Validates: string, legacy email object, and new users[] object
  //    with optional status. A blank-string status is rejected.
  isValidItems(items) {
    if (!Array.isArray(items)) return false;
    return items.every((it) => {
      if (!it || typeof it !== "object") return false;
      if (typeof it.label !== "string" || it.label.trim() === "") return false;

      const v = it.value;

      // Legacy string value
      if (typeof v === "string") return v.trim() !== "";

      // Object value (approver shape)
      if (v && typeof v === "object") {
        const levelNum = Number(v.level);
        if (!Number.isFinite(levelNum) || levelNum < 1) return false;

        // NEW multi-user shape (optional status)
        if (Array.isArray(v.users)) {
          const usersValid =
            v.users.length > 0 &&
            v.users.every(
              (e) =>
                typeof e === "string" &&
                e.trim() !== "" &&
                e.includes("@"),
            );
          if (!usersValid) return false;

          // status is optional, but if present must be a non-empty string
          if (v.status !== undefined) {
            if (typeof v.status !== "string") return false;
            if (v.status.trim() === "") return false;
          }
          return true;
        }

        // LEGACY single-email shape
        return (
          typeof v.email === "string" &&
          v.email.trim() !== "" &&
          v.email.includes("@")
        );
      }

      return false;
    });
  }

  isValid({ isCreation = false } = {}) {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    if (isCreation) {
      if (!this.id || typeof this.id !== "string" || !this.id.trim()) return false;
      if (!this.name || !this.name.trim()) return false;
      if (!SingleValueListModel.ALLOWED_SCOPES.includes(this.scope)) return false;
      if (this.scope === "user" && (!this.userId || !uuidRegex.test(this.userId))) {
        return false;
      }
      if (!this.isValidItems(this.items)) return false;
    } else {
      if (this.id !== undefined && (typeof this.id !== "string" || !this.id.trim())) return false;
      if (this.name !== undefined && !this.name.trim()) return false;
      if (
        this.scope !== undefined &&
        !SingleValueListModel.ALLOWED_SCOPES.includes(this.scope)
      )
        return false;
      if (
        this.userId !== undefined &&
        this.userId !== null &&
        !uuidRegex.test(this.userId)
      )
        return false;
      if (this.items !== undefined && !this.isValidItems(this.items)) return false;
    }
    return true;
  }

  toPayload() {
    const payload = {};
    for (const key of SingleValueListModel.ALLOWED_KEYS) {
      if (this[key] === undefined) continue;

      if (key === "metadata" && typeof this.metadata === "object") {
        const metaPayload = {};
        for (const metaKey of SingleValueListModel.ALLOWED_METADATA_KEYS) {
          if (this.metadata[metaKey] !== undefined) {
            metaPayload[metaKey] = this.metadata[metaKey];
          }
        }
        if (Object.keys(metaPayload).length > 0) payload.metadata = metaPayload;
      } else if (key === "items") {
        // ── Deep-clone object values, preserving optional status ──
        payload.items = Array.isArray(this.items)
          ? this.items.map((it) => {
              if (it.value && typeof it.value === "object") {
                // New multi-user shape (optional status)
                if (Array.isArray(it.value.users)) {
                  const baseValue = {
                    level: it.value.level,
                    users: [...it.value.users],
                  };
                  if (
                    typeof it.value.status === "string" &&
                    it.value.status.trim()
                  ) {
                    baseValue.status = it.value.status.trim();
                  }
                  return { label: it.label, value: baseValue };
                }

                // Legacy single-email shape
                return {
                  label: it.label,
                  value: { level: it.value.level, email: it.value.email },
                };
              }
              return { label: it.label, value: it.value };
            })
          : [];
      } else {
        payload[key] = this[key];
      }
    }
    return payload;
  }
}